import { useEffect, useRef, type RefObject } from "react";

const FOCUSABLE_SELECTOR = [
  "a[href]",
  "input:not([disabled]):not([type=hidden])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  "button:not([disabled])",
  "[contenteditable=true]",
  "[tabindex]:not([tabindex='-1'])",
].join(",");

/**
 * Trap keyboard focus inside `containerRef` while `isOpen` is true.
 *
 * On open: focuses the first focusable child unless something inside
 * already has focus (e.g. an `autoFocus` input).
 *
 * While open: Tab from the last focusable wraps to the first;
 * Shift+Tab from the first wraps to the last. Tab events from outside
 * the container get redirected to the first focusable.
 *
 * On close: focus returns to the element provided in `previousFocusRef`.
 * The caller is responsible for capturing the element that should
 * receive focus on close (typically `document.activeElement` at the
 * moment the open trigger fires) — capturing inside this hook is
 * unreliable because children's autoFocus effects fire before parent
 * effects in React.
 *
 * Esc handling and the modal's open/close state are the caller's job —
 * this hook only manages focus.
 */
export function useFocusTrap(
  containerRef: RefObject<HTMLElement | null>,
  isOpen: boolean,
  previousFocusRef?: RefObject<HTMLElement | null>,
) {
  const internalPreviousRef = useRef<HTMLElement | null>(null);
  const previousRef = previousFocusRef ?? internalPreviousRef;

  useEffect(() => {
    if (!isOpen) return;
    const container = containerRef.current;
    if (!container) return;
    // Internal-ref fallback: capture whatever was focused at effect time
    // (may be inside the modal if children autofocused — works for the
    // tab-wrap behavior but the caller should pass an explicit ref if
    // they want correct return-focus).
    if (!previousFocusRef) {
      previousRef.current = document.activeElement as HTMLElement | null;
    }

    // Defer initial focus so children have a chance to mount + autoFocus.
    // If the container has no focusable children, fall back to focusing
    // the container itself (caller is expected to set tabIndex={-1} or
    // similar). Otherwise focus the first focusable.
    const initialFocusId = setTimeout(() => {
      if (container.contains(document.activeElement)) return;
      const first = container.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
      (first ?? container).focus({ preventScroll: true });
    }, 0);

    const handleKey = (e: KeyboardEvent) => {
      if (e.key !== "Tab") return;
      const focusables = Array.from(
        container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
      ).filter(
        (el) => el.offsetParent !== null || el === document.activeElement,
      );
      if (!focusables.length) {
        e.preventDefault();
        return;
      }
      const first = focusables[0]!;
      const last = focusables[focusables.length - 1]!;
      const active = document.activeElement as HTMLElement | null;
      if (!container.contains(active)) {
        e.preventDefault();
        first.focus();
        return;
      }
      // Focus is on the container itself (a tabIndex={-1} dialog that took
      // programmatic focus): Tab moves to the first child naturally, but
      // Shift+Tab would walk out of the dialog, so wrap it to the last.
      if (active === container) {
        if (e.shiftKey) {
          e.preventDefault();
          last.focus();
        }
        return;
      }
      if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };

    container.addEventListener("keydown", handleKey);
    return () => {
      clearTimeout(initialFocusId);
      container.removeEventListener("keydown", handleKey);
      const prev = previousRef.current;
      if (prev && document.contains(prev)) {
        try {
          prev.focus();
        } catch {
          /* silently ignore — element may be in an unfocusable state */
        }
      }
    };
  }, [isOpen, containerRef, previousFocusRef, previousRef]);
}
